/**
 * navMetrics — single source of truth for the sticky primary-nav height.
 *
 * `#rewamp-main-nav` (RewampMainNav) is `position: sticky; top: 0`, so it
 * permanently occupies the top `REWAMP_NAV_HEIGHT` pixels of the viewport.
 * Anything ELSE that sticks inside a page (filter rails, sidebars, ToC) must
 * offset its own `top` by at least this height — otherwise it scrolls up and
 * parks *behind* the nav (which has a higher z-index) instead of just below it.
 *
 * Consumers, all deriving from this one number:
 *   - RewampMainNav → sets its own `minHeight` from REWAMP_NAV_HEIGHT.
 *   - in-page sticky (MUI sx) → `top: stickyTop(gap)` and
 *     `maxHeight: stickyMaxHeight(gap)`.
 *
 *   - App.js → mirrors REWAMP_NAV_HEIGHT into the `--rewamp-nav-height` CSS var
 *     (same documentElement pattern as its fontSize effect).
 *
 * NOTE: anchor-jump / scrollIntoView landing is handled GLOBALLY by
 * `html { scroll-padding-top: var(--rewamp-nav-height) }` in App.css — sections
 * land flush at the nav bottom (no extra gap; the `stickyTop()` gap above is for
 * sticky rails, not anchors). Landmark sections must NOT also set their own
 * scroll-margin-top — the two would stack and the section would land too low.
 *
 * If the nav height ever changes, change it HERE only.
 */
export const REWAMP_NAV_HEIGHT = 56;

/**
 * `top` (px) for a sticky element that must clear the nav, plus a breathing
 * gap below the nav. Returns a number so it drops straight into MUI `sx`.
 */
export const stickyTop = (gap = 24) => REWAMP_NAV_HEIGHT + gap;

/**
 * `maxHeight`/`height` for a full-height sticky rail: the viewport minus the
 * nav, the top gap, and an equal bottom gap.
 */
export const stickyMaxHeight = (gap = 24) =>
  `calc(100vh - ${REWAMP_NAV_HEIGHT + gap * 2}px)`;
